import ICanvasInputListener from "../logic/ICanvasInputListener";
import GeometryCommand from "./GeometryCommand";
import CommandIdEnum from "../enums/CommandIdEnums";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import RaycastIntersection from "../logic/RaycastIntersection";
import Document from "../model/document/Document";
import ISceneObject from "../model/sceneObject/ISceneObject";
import { SceneObjectStyleEnum, getSceneObjectStyle } from "../model/sceneObject/SceneObjectStyle";
import CommandStack from "../logic/CommandStack";
import EditHandleSceneObject from "../model/sceneObject/EditHandleSceneObject";
import EditHandleData from "../model/sceneObjectData/EditHandleData";
import { getSceneObjectDataFromObject3D } from "../utils/sceneObjectUtils";
import { UUID } from "../utils/typeDefinitions";
import { Matrix4, Object3D, Vector3 } from "three";
import { emitter } from "../input/AppEvents";
import { useSelectionStore } from "../store/selectionStore";
import { useHoveredEditHandleStore } from "../store/hoveredEditHandleStore";
import { getEditHandleTooltipKey } from "../utils/editHandleTooltip";
import { tolerances } from "../utils/constants";
import hasRequiredRights from "./commandUserRights";

class SelectCmd extends GeometryCommand {

    #hoverIndicatorObj: ISceneObject | undefined;
    #selectedIndicatorObj: ISceneObject | undefined;
    #handleObjects: EditHandleSceneObject[] = [];
    #hoveredHandle: EditHandleSceneObject | undefined;
    #selectedSceneObjectUuid: UUID | undefined;
    #isActive = false;
    // World view direction the current handle set was built for, so #onCameraRest can tell a
    // genuine reorientation from an ordinary pan/zoom and skip needless handle rebuilds.
    #handleViewDirection: Vector3 | undefined;

    constructor(    commandStack: CommandStack,
                    canvasInputListener: ICanvasInputListener,
                    document: Document) {
        super(CommandIdEnum.Select, commandStack, canvasInputListener, document);
        this.commandNameResourceKey = 'command.select.title';
        this.initialPrompResourceKey = 'command.select.hover';
        this.showCommandInfo = false;
    }

    start() {
        super.start();
        this.addToStack();
        this.activate();
        emitter.on('editCommandCommit', this.#onEditCommandCommit);
        emitter.on('cameraRest', this.#onCameraRest);
    }

    // Which handles are worth showing depends on the view angle, so the set is rebuilt once the
    // camera has settled. Skipped unless the view actually reoriented — a rebuild also clears the
    // hover state, so rebuilding on every pan/zoom would drop the tooltip under the cursor.
    #onCameraRest = () => {
        if (!this.#isActive || !this.#selectedSceneObjectUuid) return;

        const sceneObj = this.getDocument().getSceneObject(this.#selectedSceneObjectUuid);
        if (!sceneObj) return;

        const viewDirection = this.#getCameraWorldDirection();
        if (this.#handleViewDirection && viewDirection.angleTo(this.#handleViewDirection) < tolerances.directionAngle)
            return;

        this.hideHandles();
        this.showHandles(sceneObj);
    };

    // getCameraWorldDirection() throws when no camera is set yet. A zero vector then reads as
    // degenerate to the visibility predicates, which keeps every handle rather than breaking
    // selection outright.
    #getCameraWorldDirection(): Vector3 {
        try {
            return this.canvasInputListener.getCameraWorldDirection();
        } catch {
            return new Vector3();
        }
    }

    // An edit committed elsewhere (e.g. the Properties panel, which isn't a Command and so
    // never goes through deactivate()/activate()) — refresh the selected-indicator overlay if
    // it's showing the object that changed, since that overlay is otherwise a static snapshot
    // taken at selection time. Guarded by #isActive so this doesn't double up with the refresh
    // GeometryMoveEditCmd's own commit already gets via deactivate()/activate() on drag-end.
    #onEditCommandCommit = (event: { ownerUuid: UUID }) => {
        if (!this.#isActive || event.ownerUuid !== this.#selectedSceneObjectUuid) return;

        const sceneObj = this.getDocument().getSceneObject(this.#selectedSceneObjectUuid);
        if (!sceneObj) return;

        this.hideHandles();
        this.hideSelectedIndicator();
        this.showSelectedIndicator(sceneObj);
        this.showHandles(sceneObj);
    };

    getCursorName(): string {
        return 'default';
    }

    connectMouse() {
        super.connectMouse();

        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);

        const sceneObjectGroups = this.getDocument().sceneObjects.map(so => so.geometry.group);
        this.canvasInputListener.setTargetObjects(sceneObjectGroups);
    }

    showIndicator(sceneObject: ISceneObject) {
        this.#hoverIndicatorObj = sceneObject.clone(true);
        this.addToDrawables(this.#hoverIndicatorObj);
    }

    hideIndicator() {
        if (this.#hoverIndicatorObj) {
            this.removeAllDrawables();
            this.#hoverIndicatorObj.dispose();
            this.#hoverIndicatorObj = undefined;
        }
    }

    showSelectedIndicator(sceneObject: ISceneObject) {
        this.#selectedIndicatorObj = sceneObject.clone(true);
        this.#selectedIndicatorObj.geometry.addToScene();
        this.#selectedIndicatorObj.geometry.setStyle(getSceneObjectStyle(SceneObjectStyleEnum.Selected));
        this.#selectedIndicatorObj.onAnnotationDirectionChange(this.modelRotationOffset);
    }

    hideSelectedIndicator() {
        if (this.#selectedIndicatorObj) {
            this.#selectedIndicatorObj.geometry.removeFromScene();
            this.#selectedIndicatorObj.dispose();
            this.#selectedIndicatorObj = undefined;
        }
    }

    showHandles(sceneObject: ISceneObject) {
        const worldMatrix = sceneObject.getWorldMatrix();

        // Handles are returned in the object's local space, so the view direction is converted
        // into that space here — the one place that knows the owner's world matrix — letting each
        // scene object compare it straight against its own local axes.
        const worldViewDirection = this.#getCameraWorldDirection();
        const localViewDirection = worldViewDirection.clone()
            .transformDirection(new Matrix4().copy(worldMatrix).invert());
        this.#handleViewDirection = worldViewDirection;

        // Without update rights, don't even ask the scene object for its edit handles — there's
        // nothing the user could legitimately drag, so no handle-drag command should be launchable.
        const canEditGeometry = hasRequiredRights(this.getDocument().userRight, CommandIdEnum.EditProperties);
        const handles = canEditGeometry ? sceneObject.getEditHandles(localViewDirection) : [];
        for (const handleData of handles) {
            handleData.position.applyMatrix4(worldMatrix);
            handleData.plane?.applyMatrix4(worldMatrix);
            const handleObj = new EditHandleSceneObject(handleData);
            handleObj.addToScene();
            this.#handleObjects.push(handleObj);
        }

        // Exclude the selected object's own group so its meshes don't occlude its handles
        const selectedGroup = sceneObject.geometry.group;
        const sceneObjectGroups = this.getDocument().sceneObjects
            .filter(so => so.geometry.group !== selectedGroup)
            .map(so => so.geometry.group);
        const handleGroups = this.#handleObjects.map(h => h.geometry.group);
        this.canvasInputListener.setTargetObjects([...sceneObjectGroups, ...handleGroups]);
    }

    hideHandles() {
        this.#hoveredHandle = undefined;
        this.#handleViewDirection = undefined;
        useHoveredEditHandleStore.getState().clearHoveredEditHandle();

        for (const h of this.#handleObjects) {
            h.setHover(false);
            h.removeFromScene();
            h.dispose();
        }
        this.#handleObjects = [];
        this.canvasInputListener.resetTargetObjects();
    }

    #setHoveredHandle(handle: EditHandleSceneObject | undefined): void {
        for (const h of this.#handleObjects) {
            h.setHover(h === handle);
        }

        if (handle === this.#hoveredHandle) return;
        this.#hoveredHandle = handle;

        if (handle) {
            useHoveredEditHandleStore.getState().setHoveredEditHandle(
                handle.data.position.clone(),
                getEditHandleTooltipKey(handle.data.handleType)
            );
        } else {
            useHoveredEditHandleStore.getState().clearHoveredEditHandle();
        }
    }

    // Hides the selected-indicator overlay/handles and clears selection state. Exposed so other
    // commands (e.g. DeleteSceneObjectCmd) can deselect the object they're about to remove —
    // otherwise the indicator/handles would keep referencing a scene object that no longer exists.
    clearSelection(): void {
        this.hideHandles();
        this.hideSelectedIndicator();
        this.hideIndicator();
        this.#selectedSceneObjectUuid = undefined;
        useSelectionStore.getState().setSelection([]);
    }

    // Selects an object from outside a pointer click - CloneSceneObjectsCmd hands selection to the
    // copy it just created this way. While this command is deactivated the uuid is merely stored:
    // activate() rebuilds the indicator, handles and selection store from it, which is the same
    // path by which a selection already survives a sub-command running on top.
    selectSceneObject(uuid: UUID | undefined): void {
        this.hideHandles();
        this.hideSelectedIndicator();
        this.hideIndicator();

        const sceneObj = uuid ? this.getDocument().getSceneObject(uuid) : undefined;
        this.#selectedSceneObjectUuid = sceneObj?.data.uuid;

        if (!this.#isActive) return;

        if (sceneObj) {
            this.showSelectedIndicator(sceneObj);
            this.showHandles(sceneObj);
        }
        useSelectionStore.getState().setSelection(sceneObj ? [sceneObj.data.uuid] : []);
    }

    pointerMove(raycastResult: RaycastIntersection | undefined): boolean {
        if (!raycastResult?.object) {
            this.#setHoveredHandle(undefined);
            this.hideIndicator();
            return false;
        }

        const hoveredHandle = this.#handleObjects.find(h => h.geometry.group === raycastResult.object || h.geometry.group.children.includes(raycastResult.object!));
        this.#setHoveredHandle(hoveredHandle);
        if (hoveredHandle) {
            this.hideIndicator();
            return false;
        }

        const sceneObj = this.getSceneObjectByThreeJsObject(raycastResult.object);
        if (!sceneObj) {
            this.hideIndicator();
            return false;
        }

        // Don't show hover clone over the currently selected object
        if (sceneObj.data.uuid === this.#selectedSceneObjectUuid) {
            this.hideIndicator();
            return false;
        }

        if (sceneObj.data.uuid === this.#hoverIndicatorObj?.data.uuid) {
            return false; // Already showing the correct indicator
        }

        this.hideIndicator();
        this.showIndicator(sceneObj);
        return false;
    }

    #getClickedEditHandle(obj: Object3D): EditHandleData | undefined {
        const data = getSceneObjectDataFromObject3D(obj);
        return data instanceof EditHandleData ? data : undefined;
    }

    deactivate() {
        this.#isActive = false;

        const sceneObj = this.#selectedSceneObjectUuid
            ? this.getDocument().getSceneObject(this.#selectedSceneObjectUuid)
            : undefined;
        if (sceneObj) {
            this.hideHandles();
            this.hideSelectedIndicator();
            //this.showSelectedIndicator(sceneObj);
            //this.showHandles(sceneObj);
        }

        // Another command (e.g. an edit-handle drag) is taking over — hide the selection
        // toolbar while it's active. #selectedSceneObjectUuid is kept so activate() can restore
        // both the indicator/handles and the toolbar once this command becomes active again.
        useSelectionStore.getState().setSelection([]);

        this.disconnectMouse();
    }

    activate() {
        this.#isActive = true;

        this.setDrawableStyle(getSceneObjectStyle(SceneObjectStyleEnum.Hovered));
        this.connectMouse();

        const sceneObj = this.#selectedSceneObjectUuid
            ? this.getDocument().getSceneObject(this.#selectedSceneObjectUuid)
            : undefined;
        if (!sceneObj) return;
        this.showSelectedIndicator(sceneObj);
        this.showHandles(sceneObj);
        useSelectionStore.getState().setSelection([sceneObj.data.uuid]);
    }

    // Returns false for clicks that neither hit an edit handle nor select a scene object,
    // so they can trickle down to default click listeners
    point(raycastResult: RaycastIntersection | undefined): boolean {
        if (!raycastResult?.object) {
            this.clearSelection();
            return false;
        }

        // Handle click → launch edit command via CommandManager
        const editHandle = this.#getClickedEditHandle(raycastResult.object);
        if (editHandle) {
            this.hideHandles();
            this.hideSelectedIndicator();
            emitter.emit('commandLaunch', { commandId: editHandle.commandId, parameters: editHandle });
            return true;
        }

        // Regular scene object click
        this.hideHandles();
        this.hideSelectedIndicator();
        this.hideIndicator();

        const sceneObj = this.getSceneObjectByThreeJsObject(raycastResult.object);
        if (sceneObj) {
            this.#selectedSceneObjectUuid = sceneObj.data.uuid;
            this.showSelectedIndicator(sceneObj);
            this.showHandles(sceneObj);
            useSelectionStore.getState().setSelection([sceneObj.data.uuid]);
            return true;
        }

        this.#selectedSceneObjectUuid = undefined;
        useSelectionStore.getState().setSelection([]);
        return false;
    }

    end() {
        this.#isActive = false;
        emitter.off('editCommandCommit', this.#onEditCommandCommit);
        emitter.off('cameraRest', this.#onCameraRest);

        this.clearSelection();
        this.canvasInputListener.resetTargetObjects();
        super.end();
        super.removeFromStack();
    }

    onViewerModeChange() {
        // SelectCmd persists across viewer mode changes — do not end
    }

    canLaunch(_commandId: CommandIdEnum): boolean {
        return true;
    }

}

export default SelectCmd;
